import { formatRelativeTime } from '@doccareplus/shared/lib/date';
import type { PortalAppointmentStatus } from '../infrastructure/appointments.api';

// Pure display helpers for portal appointments. No I/O, no React — safe to unit-test in isolation.
//
// Tone classes reuse the shared `--st-*` design tokens (see packages/config/tailwind.preset.js) so an
// appointment reads with the same colour language as the clinic scheduling surfaces, without importing
// across apps (cross-app imports are forbidden — see .ai/frontend-architecture.md §1A).

/**
 * i18n key suffix for a status, mapping the C# PascalCase status name to the snake_case keys used in
 * portal.json's `appointments.status.*`.
 */
export function statusKey(status: string): string {
  return status.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();
}

interface StatusTone {
  /** Tailwind classes for the badge pill (background + text colour). */
  tone: string;
}

const STATUS_TONES: Record<PortalAppointmentStatus, StatusTone> = {
  Requested: { tone: 'bg-warning-bg text-warning' },
  Confirmed: { tone: 'bg-st-confirmed-bg text-st-confirmed' },
  CheckedIn: { tone: 'bg-st-checkedin-bg text-st-checkedin' },
  InProgress: { tone: 'bg-st-inprogress-bg text-st-inprogress' },
  Completed: { tone: 'bg-st-completed-bg text-st-completed' },
  Cancelled: { tone: 'bg-st-cancelled-bg text-st-cancelled' },
  NoShow: { tone: 'bg-st-noshow-bg text-st-noshow' },
  Rejected: { tone: 'bg-danger-bg text-danger' },
};

const FALLBACK_TONE = 'bg-surface-2 text-text-3';

/** Tailwind tone classes for a status badge, falling back to a neutral tone for unknown values. */
export function statusTone(status: string): string {
  return STATUS_TONES[status as PortalAppointmentStatus]?.tone ?? FALLBACK_TONE;
}

/** True for the statuses that still allow the patient to cancel or request a reschedule. */
export function isActionable(status: string): boolean {
  return status === 'Requested' || status === 'Confirmed';
}

/** Local date + wall-clock time for a slot, e.g. "Tue, 8 Jul · 11:00 AM". */
export function formatAppointmentDateTime(iso: string): string {
  const date = new Date(iso);
  const day = date.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short' });
  const time = date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `${day} · ${time}`;
}

/** Relative "sent" time for a pending request, e.g. "32 minutes ago". */
export function formatSentAgo(iso: string, now: Date = new Date()): string {
  return formatRelativeTime(iso, now);
}
